import React from "react";

import { useSelector } from "react-redux";

import { SaleyardPermissions } from "constants/permissions";
import { MissingFieldsIndicatorTypes } from "constants/saleLots";

import {
  getDefaultRequiredMLAFields,
  selectMLAFieldWarningsBySaleLotId,
  selectDataFieldWarningsBySaleLotId,
} from "selectors";

import { useHasSaleyardPermission, useGetMissingFieldWarnings } from "hooks";
import { MissingFieldsIndicatorBase } from "./MissingFieldsIndicator";

// For use in non formik locations where you just want to show all MLA/Data warnings e.g. on lot cards
// Use CombinedFormikMissingFieldIndicator if you want to show the warnings on a formik input.
export const MLAMissingFieldsIndicator: React.FC<{
  keyValue?: string | string[]; // cbid
}> = ({ keyValue = null }) => {
  const hasDataIndicatorsPermission = useHasSaleyardPermission(
    SaleyardPermissions.featureDataIndicators,
  );
  const hasMLAPermission = useHasSaleyardPermission(
    SaleyardPermissions.featureMLASupplementaryData,
  );

  // Use different selectors based on MLA permission
  // MLA users get actual MLA data, everyone else gets template MLA structure from sale lots
  const missingMLAFieldsLookup = useSelector(
    hasMLAPermission
      ? selectMLAFieldWarningsBySaleLotId
      : selectDataFieldWarningsBySaleLotId,
  );

  const keyValues = Array.isArray(keyValue) ? keyValue : [keyValue];
  const defaultMLAWarnings = useSelector(getDefaultRequiredMLAFields);
  const warnings = useGetMissingFieldWarnings({
    modelIds: keyValues,
    lookup: missingMLAFieldsLookup,
    defaultWarnings: defaultMLAWarnings,
  });

  // Show indicator if user has EITHER data indicators OR MLA permission
  if (
    (!hasDataIndicatorsPermission && !hasMLAPermission) ||
    defaultMLAWarnings.length === 0
  ) {
    return null;
  }

  const label = hasMLAPermission
    ? MissingFieldsIndicatorTypes.MLA
    : MissingFieldsIndicatorTypes.DATA;

  return <MissingFieldsIndicatorBase label={label} warnings={warnings} />;
};
